import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { ICarrier } from "../../interfaces/configuration";
import {
  getCarriers,
  updateCarrierStatus,
} from "../../requests/carrierRequests";
import { CarriersTable } from "../../components/carriers/CarriersTable";
import { CarrierStatusModal } from "../../components/carriers/CarrierStatusModal";
import SearchInput from "../../components/SearchInput";
import Loader from "../../components/Loader";
import { INTERNAL, MEDIUM } from "../../utils/constants";

export const CarrierManagement: React.FC = () => {
  const [carriers, setCarriers] = useState<ICarrier[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<ICarrier | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const fetchCarriers = useCallback(async (carrierName?: string) => {
    setLoading(true);
    try {
      const urlQueryParams = carrierName
        ? `?carrierName=${encodeURIComponent(carrierName)}`
        : "";
      const data = await getCarriers({
        entity: INTERNAL,
        urlQueryParams,
      });
      setCarriers(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCarriers();
  }, [fetchCarriers]);

  const handleSearch = () => {
    setActiveSearch(true);
    fetchCarriers(searchTerm);
  };

  const handleCloseSearch = () => {
    setSearchTerm("");
    setActiveSearch(false);
    fetchCarriers();
  };

  const handleToggleClick = (carrier: ICarrier) => {
    setSelectedCarrier(carrier);
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    if (!selectedCarrier) return;
    setShowConfirmModal(false);
    setUpdatingId(selectedCarrier._id);
    try {
      await updateCarrierStatus(selectedCarrier._id, !selectedCarrier.enabled);
      setCarriers((prev) =>
        prev.map((c) =>
          c._id === selectedCarrier._id
            ? { ...c, enabled: !c.enabled }
            : c,
        ),
      );
    } finally {
      setUpdatingId(null);
      setSelectedCarrier(null);
    }
  };

  const handleCancel = () => {
    setSelectedCarrier(null);
    setShowConfirmModal(false);
  };

  return (
    <Box>
      <Typography className="f-s-20 text-dark m-b-20">
        Operadores Logísticos
      </Typography>

      <Grid container alignItems="center" className="m-b-20" spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <SearchInput
            searchValue={searchTerm}
            placeholder="Buscar por nombre"
            handleSubmit={handleSearch}
            handleChange={(value: string) => setSearchTerm(value)}
            handleClose={handleCloseSearch}
            activeSearch={activeSearch}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Typography variant="body2" className="text-muted">
            {carriers.length} operador(es) encontrado(s)
          </Typography>
        </Grid>
      </Grid>

      {loading && <Loader size={MEDIUM} />}

      {!loading && carriers.length === 0 && (
        <Typography className="text-muted text-center p-20">
          No hay operadores logísticos disponibles.
        </Typography>
      )}

      {!loading && carriers.length > 0 && (
        <CarriersTable
          carriers={carriers}
          onToggle={handleToggleClick}
          updatingId={updatingId}
        />
      )}

      <CarrierStatusModal
        open={showConfirmModal}
        carrier={selectedCarrier}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        loading={updatingId !== null}
      />
    </Box>
  );
};
